import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";
import logoLyraxWhite from "../../assets/images/logo-lyrax-white.png";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
    } catch {
      setError("Credenciales inválidas. Solo el super admin puede acceder.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0f172a] text-[#cbd5e1] font-sans selection:bg-[#229E64] selection:text-[#0f172a]">
      <div className="hidden lg:flex lg:w-[55%] flex-col justify-between p-12 lg:p-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e293b]/80 via-[#0f172a] to-[#052e16]/40 z-0" />
        <div className="absolute inset-0 admin-grain opacity-50 z-0" />
        <div className="absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-[#229E64]/10 blur-3xl z-0" />

        <div className="flex items-center gap-3 z-10">
          <div className="h-10 w-10 rounded-lg bg-[#229E64] text-white font-bold flex items-center justify-center ">
            L
          </div>
          <img src={logoLyraxWhite} alt="Lyrax Logo" className="h-10 w-auto" />
        </div>

        <div className="z-10 max-w-xl mt-12 mb-auto pt-20 animate-fade-up">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#229E64] mb-4">
            Super Admin
          </p>
          <h1 className="text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6 tracking-tight">
            Panel de Administración.
          </h1>
          <p className="text-lg text-[#94a3b8] leading-relaxed max-w-md">
            Gestiona tenants, usuarios y configuración de Lyrax desde un solo lugar.
          </p>
        </div>

        <div className="z-10 flex items-center gap-16 border-t border-[#334155]/60 pt-10">
          <div>
            <div className="text-4xl font-bold text-[#229E64] mb-2 tracking-tight">100%</div>
            <div className="text-xs tracking-[0.2em] text-[#94a3b8] uppercase font-semibold">
              Control Total
            </div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-2 tracking-tight">Secure</div>
            <div className="text-xs tracking-[0.2em] text-[#94a3b8] uppercase font-semibold">
              Acceso Admin
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[45%] flex flex-col justify-center px-8 sm:px-16 lg:px-24 bg-[#1e293b] relative shadow-[-24px_0_48px_-20px_rgba(0,0,0,0.55)] z-10">
        <div className="w-full max-w-md mx-auto animate-fade-up">
          <div className="flex lg:hidden flex-col items-center mb-12 gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#229E64] text-white font-bold flex items-center justify-center text-xl">
              L
            </div>
            <img src={logoLyraxWhite} alt="Lyrax Logo" className="h-8 w-auto" />
          </div>

          <div className="mb-10">
            <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">Inicia sesión</h2>
            <p className="text-[#94a3b8]">Acceso exclusivo para administradores</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lyrax flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#94a3b8] mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-[#0f172a] border border-[#334155] rounded-lyrax text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#229E64]/40 focus:border-[#229E64] outline-none transition-all"
                  placeholder="admin@lyrax.cl"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#94a3b8] mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-[#0f172a] border border-[#334155] rounded-lyrax text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#229E64]/40 focus:border-[#229E64] outline-none transition-all"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 px-4 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
            </button>
          </form>

          <div className="mt-16 text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px bg-[#334155] flex-1" />
              <span className="text-[10px] tracking-widest text-[#94a3b8] uppercase font-bold">
                Secure Admin
              </span>
              <div className="h-px bg-[#334155] flex-1" />
            </div>
            <p className="text-xs text-[#94a3b8] leading-relaxed">
              Solo usuarios autorizados.
              <br />
              Conexiones monitoreadas y registradas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
